import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'boro-tatouaz-an-eimai-arrostos-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ αν είμαι άρρωστος;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Δεν πρέπει να κάνεις τατουάζ όταν είσαι άρρωστος. Το ανοσοποιητικό σου είναι απασχολημένο, ο κίνδυνος μόλυνσης αυξάνεται, η ποιότητα του τατουάζ επηρεάζεται και βάζεις σε κίνδυνο τον καλλιτέχνη και τους άλλους πελάτες." } },
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ με ένα ελαφρύ κρυολόγημα;", acceptedAnswer: { "@type": "Answer", text: "Ακόμα και ένα ελαφρύ κρυολόγημα είναι λόγος να αναβάλεις. Το σώμα σου χρειάζεται τους πόρους του για να θεραπευτεί, και η μακρά συνεδρία θα είναι δυσάρεστη." } },
    { "@type": "Question", name: "Πότε να επικοινωνήσω με το στούντιο αν αρρωστήσω;", acceptedAnswer: { "@type": "Answer", text: "Όσο πιο γρήγορα μπορείς. Μη περιμένεις μέχρι την τελευταία στιγμή. Ένα μήνυμα εγκαίρως δείχνει σεβασμό και βοηθάει το στούντιο να προγραμματίσει τη μέρα του καλλιτέχνη." } },
    { "@type": "Question", name: "Πόσο πρέπει να περιμένω μετά από ασθένεια πριν κάνω τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Περίμενε τουλάχιστον μερικές μέρες μέχρι μια εβδομάδα μετά την πλήρη εξαφάνιση των συμπτωμάτων, ώστε το σώμα σου να επανέλθει στα πλήρη επίπεδα ενέργειας και ανοσοποιητικής άμυνας." } },
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ με χρόνια πάθηση;", acceptedAnswer: { "@type": "Answer", text: "Πολλές φορές ναι, αλλά χρειάζεται προηγούμενη συζήτηση με τον γιατρό σου και τον καλλιτέχνη σου. Στο 210 Tattoo Athens η ομάδα αντιμετωπίζει τέτοιες περιπτώσεις με σεβασμό και σε καθοδηγεί για το τι είναι ασφαλές." } },
  ],
};

export default function BoroTatouazAnEimaiArrostosAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Μπορώ να Κάνω Τατουάζ αν Είμαι Άρρωστος;",
      "description": "Οδηγός για την Αθήνα: γιατί δεν πρέπει να κάνεις τατουάζ όταν είσαι άρρωστος, ποιες καταστάσεις χρειάζονται αναβολή, τι να κάνεις και πώς να προγραμματίσεις σωστά.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-sick-faq', faqSchema);
    inject('schema-sick-article', articleSchema);
    return () => {
      document.getElementById('schema-sick-faq')?.remove();
      document.getElementById('schema-sick-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ όταν Είσαι Άρρωστος; | 210 Tattoo Athens</title>
        <meta name="description" content="Μπορώ να κάνω τατουάζ αν είμαι άρρωστος; Γιατί χρειάζεται αναβολή, ποιες καταστάσεις υγείας το απαιτούν και πώς να προγραμματίσεις σωστά στην Αθήνα." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ όταν Είσαι Άρρωστος; | 210 Tattoo Athens" />
        <meta property="og:description" content="Γιατί δεν πρέπει να κάνεις τατουάζ όταν είσαι άρρωστος και πώς να προγραμματίσεις σωστά." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ όταν Είσαι Άρρωστος; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Γιατί χρειάζεται αναβολή και πώς να προγραμματίσεις σωστά. Οδηγός για την Αθήνα." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ όταν Είσαι Άρρωστος</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Μπορώ να κάνω τατουάζ</span>
              <br />
              <span className="text-white">αν είμαι άρρωστος;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Η ειλικρινής απάντηση είναι όχι. Δεν έχει να κάνει μόνο με την ασφάλειά σου, αλλά και με την ποιότητα του τατουάζ και τον σεβασμό προς τον καλλιτέχνη. Δες γιατί, ποιες καταστάσεις χρειάζονται αναβολή και τι να κάνεις.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why not */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί δεν πρέπει να κάνεις τατουάζ <span className="text-pink-500">όταν είσαι άρρωστος</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν είναι μια αυθαίρετη οδηγία. Υπάρχουν πραγματικοί, ιατρικοί και πρακτικοί λόγοι.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Το ανοσοποιητικό είναι ήδη απασχολημένο', desc: 'Το τατουάζ δημιουργεί χιλιάδες μικροσκοπικές πληγές που πρέπει να επουλωθούν. Όταν το ανοσοποιητικό πολεμάει ήδη μια ασθένεια, η επούλωση είναι πιο αργή και η αρρώστια μπορεί να παραταθεί.' },
                { title: 'Ο κίνδυνος μόλυνσης αυξάνεται', desc: 'Ένας οργανισμός που πολεμάει μια λοίμωξη είναι πιο ευάλωτος σε πρόσθετες μολύνσεις στη νέα, ανοιχτή πληγή.' },
                { title: 'Η ποιότητα επηρεάζεται', desc: 'Ένα άρρωστο σώμα δεν αντιδρά όπως ένα υγιές. Πιο ευαίσθητο δέρμα, πιο έντονος πόνος και περισσότερη αιμορραγία δυσκολεύουν τη δουλειά του καλλιτέχνη.' },
                { title: 'Σεβασμός προς τους άλλους', desc: 'Ο καλλιτέχνης δουλεύει από κοντά σου για ώρες. Αν είσαι μεταδοτικός, εκθέτεις σε κίνδυνο εκείνον και τους επόμενους πελάτες.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Ο αυξημένος κίνδυνος μόλυνσης είναι πραγματικός. Δες τα σημάδια στον οδηγό μας για το <Link to="/blog/pos-katalabaineis-tatouaz-molynsi-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί</Link>.
            </p>
          </div>
        </section>

        {/* Situations needing postponement */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ποιες καταστάσεις <span className="text-pink-500">χρειάζονται αναβολή</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Πυρετός', desc: 'Οποιοσδήποτε πυρετός σημαίνει ότι το ανοσοποιητικό είναι σε δράση. Μετάθεσε μέχρι να είσαι τουλάχιστον σαράντα οκτώ ώρες χωρίς πυρετό.' },
                { title: 'Κρυολόγημα και γρίπη', desc: 'Λοιμώξεις του ανώτερου αναπνευστικού είναι λόγος να αναβάλεις. Περίμενε μέχρι να νιώσεις καλά, χωρίς συμπτώματα, για μερικές μέρες.' },
                { title: 'Λοιμώξεις οποιουδήποτε τύπου', desc: 'Ουρολοιμώξεις, γαστρεντερίτιδες, ωτίτιδες, δερματικές λοιμώξεις. Ολοκλήρωσε τη θεραπεία και δώσε χρόνο να αναρρώσεις.' },
                { title: 'Πρόσφατη ασθένεια', desc: 'Ακόμα κι όταν αναρρώσεις, δώσε στον εαυτό σου λίγες μέρες επιπλέον, ιδανικά μια εβδομάδα, για να επιστρέψεις στα πλήρη επίπεδα ενέργειας.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Αν έχεις χρόνια πάθηση, όπως διαβήτη, καρδιολογικό ζήτημα ή αυτοάνοσο νόσημα, συζήτησέ το με τον γιατρό σου και τον καλλιτέχνη σου πριν προγραμματίσεις. Δεν σημαίνει απαραίτητα ότι δεν μπορείς να κάνεις τατουάζ, αλλά χρειάζεται σωστή προετοιμασία.
            </p>
          </div>
        </section>

        {/* What to do */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να κάνεις <span className="text-pink-500">αν αρρωστήσεις πριν το ραντεβού</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Επικοινώνησε άμεσα με το στούντιο, όσο πιο νωρίς μπορείς. Στείλε μήνυμα, εξήγησε την κατάσταση και ζήτα να μεταθέσεις. Τα καλά στούντιο το εκτιμούν και θα σε βοηθήσουν να βρεις νέα ημερομηνία. Αν έχεις πληρώσει προκαταβολή, συνήθως μεταφέρεται στο νέο ραντεβού, αρκεί να ειδοποιήσεις έγκαιρα.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Και τι να μην κάνεις: μην κρύψεις την κατάσταση από τον καλλιτέχνη. Μην πάρεις παρακεταμόλη λίγο πριν νομίζοντας ότι θα ξεγελάσεις την κατάσταση, τα συμπτώματα θα επιστρέψουν και κάποια παυσίπονα αραιώνουν το αίμα και δυσκολεύουν τη δουλειά. Και μην πιέζεις τον καλλιτέχνη να προχωρήσει αν εκείνος προτείνει αναβολή, εμπιστέψου την κρίση του.
            </p>
          </div>
        </section>

        {/* Planning */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να προγραμματίσεις <span className="text-pink-500">σωστά το τατουάζ σου</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Απόφυγε περιόδους επιδημιών', desc: 'Όπως η αιχμή της γρίπης τον χειμώνα, ειδικά αν είσαι επιρρεπής σε λοιμώξεις.' },
                { title: 'Δώσε χώρο πριν σημαντικά γεγονότα', desc: 'Αν έχεις ταξίδι ή γάμο, μην προγραμματίζεις το τατουάζ πολύ κοντά, ώστε να έχεις περιθώριο σε περίπτωση αναβολής.' },
                { title: 'Φρόντισε τη γενική υγεία σου', desc: 'Καλός ύπνος, σωστή διατροφή, ενυδάτωση και λιγότερο άγχος τις εβδομάδες πριν. Ένα υγιές σώμα επουλώνεται πιο ομαλά.' },
                { title: 'Πες το από την αρχή', desc: 'Αν αρρωστήσεις, ένα μήνυμα αρκεί και βρίσκουμε νέα ημερομηνία, χωρίς άγχος.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Ο σωστός προγραμματισμός βοηθάει και στην ημέρα του ραντεβού. Δες και τι <Link to="/blog/what-to-eat-before-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">να φας πριν από το τατουάζ</Link> για μια πιο ομαλή εμπειρία.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θες ένα στούντιο που παίρνει <span className="text-pink-500">την υγεία σου στα σοβαρά;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram του στούντιο, περίγραψε την ιδέα σου και προγραμμάτισε μαζί το ραντεβού σου με άνεση και σιγουριά. Και αν αρρωστήσεις πριν την ημέρα, ξέρεις τι να κάνεις.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/boro-gymnastiko-meta-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Μπορώ να Γυμναστώ μετά από Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο να περιμένεις και πώς να επιστρέψεις στη γυμναστική.</p>
              </Link>
              <Link to="/blog/what-to-eat-before-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τι να Φας πριν από το Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να φας και τι να αποφύγεις για πιο ομαλή εμπειρία.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
